import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Card, Row, Col, Typography, Descriptions, Tag, Button, 
  Tabs, Image, Space, Statistic, Table, message 
} from 'antd';
import PujaSpinner from '../../components/PujaSpinner';
import TagCategoria from '../../components/TagCategoria';
import { money } from '../../utils/format';
import {
  EditOutlined,
  ArrowLeftOutlined,
  HeartOutlined,
  HeartFilled,
  PlayCircleOutlined,
} from '@ant-design/icons';
import BotonBloqueado from '../../components/BotonBloqueado';
import { useAuth } from '../../context/AuthContext';
import { animalService, ofertaService, API_HOST } from '../../services/api';
import FamiliaresVinculados from '../../components/FamiliaresVinculados';
import CriasDelAnimal from '../../components/CriasDelAnimal';

const { Title, Text } = Typography;

// ── Árbol genealógico — solo ramas con datos ─────────────────────────
// Cuatro niveles: Animal, Padres, Abuelos, Bisabuelos. Había un quinto para
// tatarabuelos, que se sacó junto con las columnas el 2026-08-21.
const GEN_COLORS = ['#722ed1', '#1677ff', '#52c41a', '#fa8c16'];
const GEN_BG    = ['#f9f0ff', '#e6f4ff', '#f6ffed', '#fff7e6'];

const buildGeneraciones = (g) => [
  [
    g.padres?.padre  && { label: 'Padre',  value: g.padres.padre,  gen: 1, linea: 'P' },
    g.padres?.madre  && { label: 'Madre',  value: g.padres.madre,  gen: 1, linea: 'M' },
  ],
  [
    g.abuelos?.paterno?.abuelo && { label: 'Abuelo P.',  value: g.abuelos.paterno.abuelo,  gen: 2, linea: 'P' },
    g.abuelos?.paterno?.abuela && { label: 'Abuela P.',  value: g.abuelos.paterno.abuela,  gen: 2, linea: 'P' },
    g.abuelos?.materno?.abuelo && { label: 'Abuelo M.',  value: g.abuelos.materno.abuelo,  gen: 2, linea: 'M' },
    g.abuelos?.materno?.abuela && { label: 'Abuela M.',  value: g.abuelos.materno.abuela,  gen: 2, linea: 'M' },
  ],
  [
    g.bisabuelos?.paterno?.bisabuelo && { label: 'Bisabuelo P.',  value: g.bisabuelos.paterno.bisabuelo,  gen: 3, linea: 'P' },
    g.bisabuelos?.paterno?.bisabuela && { label: 'Bisabuela P.',  value: g.bisabuelos.paterno.bisabuela,  gen: 3, linea: 'P' },
    g.bisabuelos?.materno?.bisabuelo && { label: 'Bisabuelo M.',  value: g.bisabuelos.materno.bisabuelo,  gen: 3, linea: 'M' },
    g.bisabuelos?.materno?.bisabuela && { label: 'Bisabuela M.',  value: g.bisabuelos.materno.bisabuela,  gen: 3, linea: 'M' },
  ],
].map(gen => gen.filter(Boolean)).filter(gen => gen.length > 0);

// A nivel de módulo, NO dentro de PedigreeTree: un componente definido en el
// render del padre cambia de identidad en cada render, y React lo desmonta y
// lo vuelve a montar en vez de actualizarlo. `AnimalPage.jsx` ya seguía esta
// regla y hasta dejaba escrito el motivo; acá faltaba.
const Nodo = ({ label, value, gen, linea, prevLinea }) => {
  const c  = GEN_COLORS[gen];
  const bg = GEN_BG[gen];
  return (
    <div style={{ marginTop: prevLinea === 'P' && linea === 'M' ? 10 : 4 }}>
      <div style={{
        border: `1px solid ${c}40`,
        borderLeft: `3px solid ${c}`,
        borderRadius: '0 6px 6px 0',
        padding: '5px 10px',
        background: bg,
        overflow: 'hidden',
      }}>
        <div style={{ fontSize: 10, color: c, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.2 }}>
          {label}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#262626', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
          {value}
        </div>
      </div>
    </div>
  );
};

const PedigreeTree = ({ g, animalName }) => {
  const generaciones = buildGeneraciones(g);
  if (generaciones.length === 0) return <Text type="secondary">Sin genealogía registrada</Text>;

  return (
    <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
        {[['Animal', 0], ['Padres', 1], ['Abuelos', 2], ['Bisabuelos', 3]].map(([lbl, gen]) => (
          <span key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#555' }}>
            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: GEN_COLORS[gen], opacity: 0.8 }} />
            {lbl}
          </span>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 0, minWidth: 'max-content' }}>
        {/* Animal */}
        <div style={{ width: 140, flexShrink: 0, paddingRight: 8 }}>
          <div style={{
            border: `1px solid ${GEN_COLORS[0]}40`,
            borderLeft: `3px solid ${GEN_COLORS[0]}`,
            borderRadius: '0 6px 6px 0',
            padding: '8px 10px',
            background: GEN_BG[0],
          }}>
            <div style={{ fontSize: 10, color: GEN_COLORS[0], fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Animal</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#262626', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {animalName}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{ width: 1, alignSelf: 'stretch', background: '#e0e0e0', marginRight: 16, flexShrink: 0 }} />

        {/* Generaciones — solo las que tienen datos */}
        {generaciones.map((gen, gi) => (
          <div key={gi} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 140, flexShrink: 0, marginRight: gi < generaciones.length - 1 ? 16 : 0 }}>
            {gen.map((nodo, ni) => (
              <Nodo key={ni} {...nodo} prevLinea={ni > 0 ? gen[ni - 1].linea : null} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const AnimalDetalle = () => {
  const { tieneRecurso } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  // Lee ?tab=<key> de la URL para que rutas como /animales/12?tab=familiares
  // abran directamente esa pestaña (lo usa AnimalForm para 'Gestionar familiares').
  const [searchParams] = useSearchParams();
  const tabInicial = searchParams.get('tab') || 'info';
  const [loading, setLoading] = useState(true);
  const [animal, setAnimal] = useState(null);
  const [ofertas, setOfertas] = useState([]);
  const [seguido, setSeguido] = useState(false);

  useEffect(() => {
    fetchAnimal();
    fetchOfertas();
  }, [id]);

  const fetchAnimal = async () => {
    setLoading(true);
    try {
      const response = await animalService.getById(id);
      setAnimal(response.data);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar animal');
      navigate('/animales');
    } finally {
      setLoading(false);
    }
  };

  const fetchOfertas = async () => {
    try {
      const response = await ofertaService.getByAnimal(id);
      setOfertas(response.data.ofertas || []);
    } catch (error) {
      console.error('Error al cargar ofertas:', error);
      message.error(error.response?.data?.error || 'Error al cargar ofertas');
    }
  };

  const handleSeguimiento = async () => {
    try {
      if (seguido) {
        await ofertaService.removeSeguimiento(id);
        message.success('Eliminado de favoritos');
      } else {
        await ofertaService.addSeguimiento(id);
        message.success('Agregado a favoritos');
      }
      setSeguido(!seguido);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error');
    }
  };

  const ofertasColumns = [
    {
      title: 'Fecha',
      dataIndex: 'fecha_oferta',
      key: 'fecha',
    },
    {
      title: 'Hora',
      dataIndex: 'hora_oferta',
      key: 'hora',
    },
    {
      title: 'Usuario',
      key: 'usuario',
      render: (_, record) => record.usuario?.nombre || '-',
    },
    {
      title: 'Monto',
      dataIndex: 'monto_oferta',
      key: 'monto',
      render: (value) => (
        <Text strong style={{ color: '#52c41a' }}>{money(value)}</Text>
      ),
    },
  ];

  if (loading) {
    return (
      <PujaSpinner size="lg" />
    );
  }

  if (!animal) return null;

  const tabItems = [
    {
      key: 'info',
      label: 'Información General',
      children: (
        <Row gutter={[24, 24]}>
          <Col xs={24} md={12}>
            <Descriptions title="Datos Básicos" bordered column={1}>
              <Descriptions.Item label="Nombre">{animal.nom_animal}</Descriptions.Item>
              <Descriptions.Item label="RP">{animal.rp_animal}</Descriptions.Item>
              <Descriptions.Item label="Corral">{animal.corral}</Descriptions.Item>
              <Descriptions.Item label="Categoría">
                <TagCategoria valor={animal.categoria?.nom_categoria} />
              </Descriptions.Item>
              {animal.nom_raza && (
                <Descriptions.Item label="Raza">
                  <Tag color="green">{animal.nom_raza}</Tag>
                </Descriptions.Item>
              )}
              {/* SIEMPRE, con cabaña o sin ella: la ficha no la mostraba y el
                  productor no tenia forma de ver que el animal no iba a entrar
                  al catalogo. `nombre_publico` primero, igual que el selector
                  del formulario y que `Animal.resolver_procedencia()`. */}
              <Descriptions.Item label="Cabaña">
                {animal.cabana
                  ? (animal.cabana.nombre_publico || animal.cabana.nom_cabana)
                  : <Text type="warning">Sin cabaña — no entra al catálogo de genética</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha Nacimiento">{animal.fecha_nac}</Descriptions.Item>
              <Descriptions.Item label="Registro">{animal.registro}</Descriptions.Item>
              <Descriptions.Item label="Caravana">{animal.caravana || '-'}</Descriptions.Item>
              <Descriptions.Item label="HBA">{animal.hba_animal || '-'}</Descriptions.Item>
              <Descriptions.Item label="ADN">{animal.adn_animal || '-'}</Descriptions.Item>
            </Descriptions>
          </Col>
          <Col xs={24} md={12}>
            <Descriptions title="Características" bordered column={1}>
              <Descriptions.Item label="Cabeza">{animal.cabeza || '-'}</Descriptions.Item>
              <Descriptions.Item label="Pelo">{animal.pelo || '-'}</Descriptions.Item>
              <Descriptions.Item label="Frame">{animal.frame || '-'}</Descriptions.Item>
              <Descriptions.Item label="Peso al Nacer">{animal.peso_nacer ? `${animal.peso_nacer} kg` : '-'}</Descriptions.Item>
              <Descriptions.Item label="Peso Actual">{animal.peso_actual ? `${animal.peso_actual} kg` : '-'}</Descriptions.Item>
              <Descriptions.Item label="CE Actual">{animal.ce_actual || '-'}</Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      ),
    },
    {
      key: 'deps',
      label: 'DEPs y Ranking',
      children: animal.deps ? (
        <Row gutter={[24, 24]}>
          <Col xs={24} md={8}>
            <Card title="DEPs" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Nacer">{animal.deps.nacer || '-'}</Descriptions.Item>
                <Descriptions.Item label="Destete">{animal.deps.destete || '-'}</Descriptions.Item>
                <Descriptions.Item label="Ap. Materna">{animal.deps.ap_materna || '-'}</Descriptions.Item>
                <Descriptions.Item label="LyC">{animal.deps.lyc || '-'}</Descriptions.Item>
                <Descriptions.Item label="Final">{animal.deps.final || '-'}</Descriptions.Item>
                <Descriptions.Item label="CE">{animal.deps.ce || '-'}</Descriptions.Item>
                <Descriptions.Item label="AOB">{animal.deps.aob || '-'}</Descriptions.Item>
                <Descriptions.Item label="GD">{animal.deps.gd || '-'}</Descriptions.Item>
                <Descriptions.Item label="GC">{animal.deps.gc || '-'}</Descriptions.Item>
                <Descriptions.Item label="%GI">{animal.deps.porcgi || '-'}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card title="Precisión" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Nacer">{animal.precision?.nacer || '-'}</Descriptions.Item>
                <Descriptions.Item label="Destete">{animal.precision?.destete || '-'}</Descriptions.Item>
                <Descriptions.Item label="Ap. Materna">{animal.precision?.ap_materna || '-'}</Descriptions.Item>
                <Descriptions.Item label="LyC">{animal.precision?.lyc || '-'}</Descriptions.Item>
                <Descriptions.Item label="Final">{animal.precision?.final || '-'}</Descriptions.Item>
                <Descriptions.Item label="CE">{animal.precision?.ce || '-'}</Descriptions.Item>
                <Descriptions.Item label="AOB">{animal.precision?.aob || '-'}</Descriptions.Item>
                <Descriptions.Item label="GD">{animal.precision?.gd || '-'}</Descriptions.Item>
                <Descriptions.Item label="GC">{animal.precision?.gc || '-'}</Descriptions.Item>
                <Descriptions.Item label="%GI">{animal.precision?.porcgi || '-'}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card title="Ranking" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Nacer">{animal.ranking?.nacer || '-'}</Descriptions.Item>
                <Descriptions.Item label="Destete">{animal.ranking?.destete || '-'}</Descriptions.Item>
                <Descriptions.Item label="Ap. Materna">{animal.ranking?.ap_materna || '-'}</Descriptions.Item>
                <Descriptions.Item label="LyC">{animal.ranking?.lyc || '-'}</Descriptions.Item>
                <Descriptions.Item label="Final">{animal.ranking?.final || '-'}</Descriptions.Item>
                <Descriptions.Item label="CE">{animal.ranking?.ce || '-'}</Descriptions.Item>
                <Descriptions.Item label="AOB">{animal.ranking?.aob || '-'}</Descriptions.Item>
                <Descriptions.Item label="GD">{animal.ranking?.gd || '-'}</Descriptions.Item>
                <Descriptions.Item label="GC">{animal.ranking?.gc || '-'}</Descriptions.Item>
                <Descriptions.Item label="%GI">{animal.ranking?.porcgi || '-'}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
      ) : (
        <Text type="secondary">Sin datos de DEPs registrados</Text>
      ),
    },
    {
      key: 'genealogia',
      label: 'Genealogía',
      children: animal.genealogia
        ? <PedigreeTree g={animal.genealogia} animalName={animal.nom_animal} />
        : <Text type="secondary">Sin genealogía registrada</Text>,
    },
    {
      key: 'familiares',
      label: 'Familiares',
      children: <FamiliaresVinculados animalId={animal.id_animal} />,
    },
    // Sólo hembras, con el mismo criterio que el botón del formulario
    // (`sexo === 'F'`, la de su categoría). Las crías al pie: en una ELECCIÓN
    // la madre sale con varios terneros (2026-10-21).
    ...(animal.sexo === 'F' ? [{
      key: 'crias',
      label: 'Crías',
      children: <CriasDelAnimal animal={animal} />,
    }] : []),
    {
      key: 'ofertas',
      label: `Ofertas (${ofertas.length})`,
      children: (
        <Table
          scroll={{ x: 'max-content' }}
          columns={ofertasColumns}
          dataSource={ofertas}
          rowKey="id_oferta"
          pagination={false}
          size="small"
        />
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      {/* `gutter={[0, 12]}` — solo vertical: cuando los dos Col se apilan en
          mobile no habia NADA de separacion entre ellos. Horizontal queda en 0
          para no correr el layout de desktop, que hoy anda bien. */}
      <Row justify="space-between" align="middle" gutter={[0, 12]} style={{ marginBottom: 16 }}>
        <Col>
          {/* `wrap` NO ES OPCIONAL ACA. Un <Space> sin el no envuelve: APRIETA.
              Sus hijos son items de flex en una sola linea, asi que en un
              telefono, entre el boton Volver (~90px) y los dos tags (~220px),
              al titulo le quedaban ~50px y el nombre del animal se renderizaba
              UNA LETRA POR LINEA, en vertical. Con `wrap` el titulo se va a su
              propia linea, donde tiene el ancho completo y corta por palabras
              como corresponde. En desktop sobra lugar, asi que no cambia nada. */}
          <Space wrap>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/animales')}>
              Volver
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {animal.nom_animal}
            </Title>
            <TagCategoria valor={animal.categoria?.nom_categoria} />
            {animal.nom_raza && <Tag color="green">{animal.nom_raza}</Tag>}
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Button 
              icon={seguido ? <HeartFilled /> : <HeartOutlined />}
              onClick={handleSeguimiento}
              style={{ color: seguido ? '#ff4d4f' : undefined }}
            >
              {seguido ? 'Siguiendo' : 'Seguir'}
            </Button>
            {/* Mismo criterio que en la grilla: el unico eje es el plan.
                `Animal` es PERMEABLE_POR_INVITACION, asi que si llegaste a ver
                esta ficha el animal es tuyo o lo gestionas, y en los dos casos
                podes escribirlo. Lo que puede faltar es `crud_animales`. */}
            {tieneRecurso('crud_animales') ? (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => navigate(`/animales/${id}/editar`)}
              >
                Editar
              </Button>
            ) : (
              <BotonBloqueado motivo="plan" type="primary" icon={<EditOutlined />}>
                Editar
              </BotonBloqueado>
            )}
          </Space>
        </Col>
      </Row>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            {/* `formatter` identidad, igual que RP: sin el, <Statistic> agrupa
                los numeros y un corral 1234 se mostraria como "1,234". */}
            <Statistic title="Corral" value={animal.corral || '-'} formatter={(v) => v} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="RP" value={animal.rp_animal} formatter={(v) => v} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            {/* `?? null` y NO `|| 0`. El `|| 0` no dejaba que `money()` viera
                nunca el vacio: el mismo animal sin precio mostraba "—" en el
                listado y "$0" aca, a un click de distancia.

                Y el `null` explicito no es de mas: <Statistic> declara
                `value = 0` como parametro POR DEFECTO, y un default de JS se
                dispara con `undefined`. El `null` lo atraviesa; un `undefined`
                se convertiria en 0 y volveriamos al "$0". */}
            <Statistic 
              title="Precio Base" 
              value={animal.precio_base ?? null}
              formatter={(v) => money(v)}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            {/* Aca el `?? null` es OBLIGATORIO: sin ofertas, `ofertas[0]?.x`
                devuelve `undefined`, que cae en el default `value = 0` de
                <Statistic> y volveria a mostrar "$0". Y "$0" no dice "nadie
                oferto": dice "alguien oferto cero", en la misma pantalla donde
                el tab de al lado dice "Ofertas (0)". */}
            <Statistic 
              title="Mejor Oferta" 
              value={ofertas[0]?.monto_oferta ?? null}
              valueStyle={{ color: '#52c41a' }}
              formatter={(v) => money(v)}
            />
          </Card>
        </Col>
      </Row>

      {/* Contenido con tabs */}
      <Row gutter={24}>
        {/* Imágenes */}
        <Col xs={24} lg={8}>
          <Card title="Imágenes">
            {animal.imagenes && animal.imagenes.length > 0 ? (
              <Image.PreviewGroup>
                <Row gutter={[8, 8]}>
                  {animal.imagenes.map((img, index) => (
                    <Col span={24} key={img.id_imagen || index}>
                      <Image
                        src={img.url_imagen?.startsWith('http') ? img.url_imagen : `${API_HOST}${img.url_imagen}`}
                        alt={img.nom_imagen || `Imagen ${index + 1}`}
                        style={{ width: '100%', borderRadius: 8 }}
                      />
                    </Col>
                  ))}
                </Row>
              </Image.PreviewGroup>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                Sin imágenes
              </div>
            )}
            
            {animal.codigo_video && (
              <Button 
                type="link" 
                icon={<PlayCircleOutlined />}
                href={animal.codigo_video}
                target="_blank"
                style={{ marginTop: 16 }}
              >
                Ver Video
              </Button>
            )}
          </Card>
        </Col>

        {/* Información */}
        <Col xs={24} lg={16}>
          <Card>
            <Tabs items={tabItems} defaultActiveKey={tabInicial} />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AnimalDetalle;
